import CustomTooltip from '../../../shared/CustomTooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    Avatar,
    Chip,
    Grid,
    InputLabel,
    ListItemAvatar,
    ListItemText,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import FormMultiSelectWithALL from '../../../shared/FormMultiSelectWithALL';

const BillingExclusionForm = ({
    index,
    handleDelete,
    allGuests,
    availableModules,
    currentExclusions,
}: {
    index: number;
    handleDelete: (i: number) => void;
    allGuests: any[];
    availableModules: any;
    currentExclusions: any[];
}) => {
    const { control, setValue } = useFormContext();

    const isAll = {
        value: 'ALL',
        label: 'All Current & Future Listings',
    };

    const isNone = {
        value: 'NONE',
        label: 'None',
    };

    const isCurrent = {
        value: 'CURRENT',
        label: 'All Current Listings',
    };

    const theme = useTheme();

    return (
        <Stack
            spacing={2}
            border={1}
            borderColor={theme.palette.lightGrey.main}
            p={2}
            borderRadius={2}
        >
            <Grid container spacing={2} alignItems='flex-start'>
                {/* Guest Label */}
                <Grid item xs={12} lg={3}>
                    <InputLabel required>
                        Guest{' '}
                        <CustomTooltip
                            title={
                                <Typography>
                                    Applying a billing exclusion ensures all
                                    future bookings for that guest are exempt
                                    from charges.
                                </Typography>
                            }
                            placement='right'
                        >
                            <HelpOutlineIcon
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '16px',
                                }}
                            />
                        </CustomTooltip>
                    </InputLabel>
                </Grid>

                {/* Guest Field */}
                <Grid item xs={12} lg={9}>
                    <FormAutocomplete<{ value: number; label: string }>
                        name={`exclusions[${index}].guest.id`}
                        control={control}
                        options={
                            allGuests
                                ?.filter((g) => g.isActive)
                                .map((g) => ({
                                    value: g.id,
                                    label: g.name,
                                })) || []
                        }
                        placeholder='Select a guest'
                        onOptionSelect={(selectedGuest) => {
                            const guestId = selectedGuest?.value;
                            const matched = currentExclusions?.find(
                                (ex: any) => ex.guest.id === guestId,
                            );

                            if (matched) {
                                const formattedModules =
                                    matched.modules.length === 0
                                        ? [
                                              {
                                                  value: 'ALL',
                                                  label: 'All current and future listings',
                                              },
                                          ]
                                        : matched.modules.map((mod: any) => ({
                                              value: mod.id,
                                              label: mod.name,
                                          }));

                                setValue(
                                    `exclusions[${index}].modules`,
                                    formattedModules,
                                );
                            } else {
                                setValue(`exclusions[${index}].modules`, []);
                            }
                        }}
                    />
                </Grid>

                {/* Experiences Label */}
                <Grid item xs={12} lg={3}>
                    <InputLabel required>
                        Experiences{' '}
                        <CustomTooltip
                            title={
                                <Typography>
                                    Choose which experiences guest companies
                                    won’t be charged for.
                                </Typography>
                            }
                            placement='right'
                        >
                            <HelpOutlineIcon
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '16px',
                                }}
                            />
                        </CustomTooltip>
                    </InputLabel>
                </Grid>

                {/* Experiences Field */}
                <Grid item xs={12} lg={9}>
                    <Controller
                        name={`exclusions[${index}].modules`}
                        control={control}
                        render={({ field: { onChange, value = [] } }) => {
                            // Detect if we're in ALL mode (empty array = ALL)
                            const isAllSelected =
                                value.length > 0 && value[0]?.value === 'ALL';

                            const currentListings =
                                availableModules
                                    ?.filter((m: any) => m.isAvailable)
                                    .map((m: any) => ({
                                        value: m.id,
                                        label: m.name,
                                        ...m,
                                    })) || [];

                            // If ALL selected, inject current listings for display only
                            const displayValue = isAllSelected
                                ? currentListings
                                : value;

                            return (
                                <FormMultiSelectWithALL
                                    name={`exclusions[${index}].modules`}
                                    control={control}
                                    options={[
                                        isAll,
                                        isCurrent,
                                        ...(value.length > 0 ? [isNone] : []),
                                        ...currentListings,
                                    ]}
                                    placeholder='Select experiences'
                                    value={displayValue}
                                    renderTags={(selected, getTagProps) => {
                                        const displayed = selected.slice(0, 4);
                                        const extraCount =
                                            selected.length - displayed.length;

                                        return [
                                            ...displayed.map(
                                                (option: any, index) => (
                                                    <Chip
                                                        {...getTagProps({
                                                            index,
                                                        })}
                                                        key={index}
                                                        size='small'
                                                        label={option.label}
                                                    />
                                                ),
                                            ),
                                            isAllSelected && (
                                                <>
                                                    <Chip
                                                        key='extra'
                                                        size='small'
                                                        variant='outlined'
                                                        label={`+${extraCount}`}
                                                    />
                                                    <Chip
                                                        key='future'
                                                        size='small'
                                                        variant='outlined'
                                                        label='+ future listings'
                                                    />
                                                </>
                                            ),
                                            extraCount > 0 &&
                                                !isAllSelected && (
                                                    <Chip
                                                        key='extra'
                                                        size='small'
                                                        variant='outlined'
                                                        label={`+${extraCount}`}
                                                    />
                                                ),
                                        ];
                                    }}
                                    renderOption={(props, option: any) => {
                                        const activeCount =
                                            availableModules?.filter(
                                                (m: any) => m.isAvailable,
                                            )?.length ?? 0;

                                        if (option.value === 'ALL') {
                                            return (
                                                <li {...props} key='ALL'>
                                                    <ListItemText
                                                        primary='All Current and Future Listings'
                                                        secondary={`Includes all ${activeCount} and future available experiences`}
                                                    />
                                                </li>
                                            );
                                        }

                                        if (option.value === 'CURRENT') {
                                            return (
                                                <li {...props} key='CURRENT'>
                                                    <ListItemText
                                                        primary='All Current Listings'
                                                        secondary={`Includes all current ${activeCount} available experiences`}
                                                    />
                                                </li>
                                            );
                                        }

                                        if (option.value === 'NONE') {
                                            return (
                                                <li {...props} key='NONE'>
                                                    <ListItemText
                                                        primary='None'
                                                        secondary='Clear all selected experiences'
                                                    />
                                                </li>
                                            );
                                        }

                                        return (
                                            <li {...props} key={option.id}>
                                                <ListItemAvatar>
                                                    <Avatar
                                                        variant='rounded'
                                                        src={
                                                            option.photos?.[0]
                                                                ?.src
                                                        }
                                                    />
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={option.name}
                                                    secondary={`${
                                                        option.streetAddress ??
                                                        ''
                                                    }, ${option.suburb ?? ''}`}
                                                />
                                            </li>
                                        );
                                    }}
                                    onChange={(_, selectedOptions) => {
                                        const selectedValues =
                                            selectedOptions.map(
                                                (opt: any) => opt.value,
                                            );

                                        if (selectedValues.includes('NONE')) {
                                            onChange([
                                                {
                                                    value: 'NONE',
                                                    label: 'None',
                                                },
                                            ]);
                                            return;
                                        }

                                        if (selectedValues.includes('ALL')) {
                                            onChange([
                                                {
                                                    value: 'ALL',
                                                    label: 'All Current and Future Listings',
                                                },
                                            ]);
                                            return;
                                        }

                                        if (
                                            selectedValues.includes('CURRENT')
                                        ) {
                                            const allSelected = availableModules
                                                ?.filter(
                                                    (m: any) => m.isAvailable,
                                                )
                                                .map((m: any) => ({
                                                    value: m.id,
                                                    label: m.name,
                                                    ...m,
                                                }));
                                            onChange(allSelected);
                                            return;
                                        }

                                        const cleaned = selectedOptions.filter(
                                            (opt: any) =>
                                                opt.value !== 'ALL' &&
                                                opt.value !== 'CURRENT' &&
                                                opt.value !== 'NONE',
                                        );
                                        onChange(cleaned);
                                    }}
                                />
                            );
                        }}
                    />
                </Grid>
            </Grid>
        </Stack>
    );
};

export default BillingExclusionForm;
